/**
 * CollapsibleSection (R133) — slim expand/collapse wrapper for the Dashboard's
 * stacked panels. The expanded/collapsed state persists per section in
 * localStorage so the user's preferred layout survives reloads.
 *
 * R151 — "new entry" attention. Each section tracks the entry `count` it last
 * showed the user (persisted per id). While a section is COLLAPSED and its
 * count rises above that last-seen value, the header switches to an amber,
 * gently-pulsing "N new" state so the user notices fresh entries (a new alert,
 * a new pending watch, etc.) landing in a folded section. Expanding the section
 * marks everything seen and clears the attention.
 *
 * The page's scroll anchors live OUTSIDE this wrapper so the left-rail Jump
 * strip still works when a section is closed.
 *
 * 2026-09-02 — ONE HEADER PER SECTION. Every one of the eight sections used to
 * print its title twice: here, and again as a `variant="h6"` inside the card
 * immediately below. `info` and `actions` exist so the card's help icon and its
 * buttons can live in THIS row instead, leaving the card to carry only content.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAppTheme } from '../../hooks/useAppTheme';
import { CH_TYPE, chSectionTitleSx } from '../../styles/appUi';

const LS_PREFIX = 'dash-collapse-';
const SEEN_PREFIX = 'dash-seen-';

interface Props {
  /** Stable id — localStorage key + test hook. */
  id: string;
  title: string;
  /** Optional live entry count. Drives the badge + the "new entry" attention. */
  count?: number;
  defaultExpanded?: boolean;
  /** Help affordance for the section (the card's old info icon). Sits beside the
   *  title and does not toggle the section. */
  info?: React.ReactNode;
  /** Right-aligned controls for the section (the card's old button row). Only
   *  rendered while OPEN — they operate on content that is not visible when the
   *  section is folded. Clicks never toggle the section. */
  actions?: React.ReactNode;
  /** One-line summary shown on the right while COLLAPSED, in place of the
   *  generic "collapsed — click to expand". */
  collapsedHint?: React.ReactNode;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<Props> = ({ id, title, count, defaultExpanded = true, info, actions, collapsedHint, children }) => {
  const { C } = useAppTheme();
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(LS_PREFIX + id);
      return v == null ? defaultExpanded : v === '1';
    } catch { return defaultExpanded; }
  });

  // Last count the user has "seen" (i.e. had the section open at). Persisted so
  // a fresh entry that arrived while a section was collapsed is still flagged
  // across reloads.
  const [lastSeen, setLastSeen] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem(SEEN_PREFIX + id) || '', 10);
      return Number.isFinite(v) ? v : 0;
    } catch { return 0; }
  });

  const persistSeen = useCallback((n: number) => {
    try { localStorage.setItem(SEEN_PREFIX + id, String(n)); } catch { /* ignore */ }
  }, [id]);

  // Keep the seen-count in sync: while OPEN the user is looking at the section
  // so everything counts as seen; while COLLAPSED we only ratchet DOWN (entries
  // removed) so a later rise re-flags — we never silently raise it.
  useEffect(() => {
    if (count == null) return;
    if (open) {
      if (lastSeen !== count) { setLastSeen(count); persistSeen(count); }
    } else if (count < lastSeen) {
      setLastSeen(count); persistSeen(count);
    }
  }, [open, count, lastSeen, persistSeen]);

  const toggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_PREFIX + id, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, [id]);

  const hasNew = !open && count != null && count > lastSeen;
  const newCount = hasNew ? count! - lastSeen : 0;
  const warn = C.warn;

  return (
    <Box sx={{ mb: open ? 0 : 1 }}>
      <Box
        onClick={toggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
          px: 1.25, py: 0.6, mb: 0.5, borderRadius: 1.5, userSelect: 'none',
          transition: 'background-color 120ms ease',
          // R150 — soft, low-strain primary tint by default.
          // R151 — when collapsed with new entries, switch to an amber pulsing
          // "attention" treatment so fresh entries in a folded section get noticed.
          color: 'text.primary',
          ...(hasNew
            ? {
                background: `linear-gradient(90deg, ${alpha(warn, 0.20)} 0%, ${alpha(warn, 0.06)} 100%)`,
                borderLeft: `3px solid ${warn}`,
                border: `1px solid ${alpha(warn, 0.55)}`,
                animation: 'dashSectionPulse 1.7s ease-in-out infinite',
                '@keyframes dashSectionPulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(warn, 0)}` },
                  '50%':      { boxShadow: `0 0 0 3px ${alpha(warn, 0.32)}` },
                },
                '&:hover': { background: alpha(warn, 0.26) },
              }
            : {
                background: theme => `linear-gradient(90deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04)} 100%)`,
                borderLeft: theme => `3px solid ${theme.palette.primary.main}`,
                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                '&:hover': { background: theme => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.16) },
              }),
        }}
      >
        {open
          ? <ExpandMoreIcon sx={{ fontSize: 19, color: 'primary.main', opacity: 0.95 }} />
          : <ChevronRightIcon sx={{ fontSize: 19, color: hasNew ? warn : 'primary.main', opacity: 0.95 }} />}
        <Typography sx={{ ...chSectionTitleSx, color: 'inherit' }}>{title}</Typography>
        {count != null && count > 0 && (
          <Chip label={count} size="small"
            sx={{
              height: 18, fontSize: CH_TYPE.micro, fontWeight: 700,
              bgcolor: hasNew ? warn : 'primary.main', color: hasNew ? '#fff' : 'primary.contrastText',
              '& .MuiChip-label': { px: 0.6 },
            }} />
        )}
        {/* The card below used to own this icon; it belongs with the title. */}
        {info && <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>{info}</Box>}
        {/* Section controls. Open only — they act on rows you cannot see when
            the section is folded, and a stray click must not collapse it. */}
        {open && actions && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          >
            {actions}
          </Box>
        )}
        {hasNew && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 'auto' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: warn, boxShadow: `0 0 0 3px ${alpha(warn, 0.25)}` }} />
            <Typography sx={{ fontSize: CH_TYPE.micro, color: warn, fontWeight: 800, letterSpacing: 0.2 }}>
              {newCount} new — click to expand
            </Typography>
          </Box>
        )}
        {!open && !hasNew && (
          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', opacity: 0.95, ml: 'auto', fontStyle: 'italic' }}>
            {collapsedHint || 'collapsed — click to expand'}
          </Typography>
        )}
      </Box>
      {open && children}
    </Box>
  );
};

export default CollapsibleSection;
